import { Building2, Users, Award, Target } from 'lucide-react';
import { useState, useEffect } from 'react';
import Breadcrumb from '@/components/Breadcrumb';
import SEO from '@/components/SEO';

const About = () => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await fetch('/api/about-content-public');
      if (response.ok) {
        const data = await response.json();
        setContent(data);
      }
    } catch (error) {
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">Yükleniyor...</div>
      </div>
    );
  }

  if (!content) return null;

  const values = [
    { icon: Building2, title: content.value1_title, description: content.value1_desc },
    { icon: Users, title: content.value2_title, description: content.value2_desc },
    { icon: Award, title: content.value3_title, description: content.value3_desc },
    { icon: Target, title: content.value4_title, description: content.value4_desc },
  ];

  return (
    <>
    <SEO 
      title="Kurumsal"
      description="MİGA Yapı hakkında. 30 yılı aşkın deneyimimizle İstanbul'da kentsel dönüşüm ve inşaat alanında öncü firmayız. Misyonumuz, vizyonumuz ve değerlerimiz."
      keywords="miga yapı hakkında, kurumsal, inşaat firması, kentsel dönüşüm firması, istanbul inşaat şirketi, güvenilir inşaat"
    />
    <div data-testid="about-page" className="min-h-screen pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <Breadcrumb items={[{ label: 'Kurumsal' }]} />
        
        {/* Header */}
        <div className="text-center mb-16">
          <h1
            data-testid="about-title"
            className="heading-font text-5xl sm:text-6xl font-bold text-[#1e4a7a] mb-6"
          >
            {content.title}
          </h1>
          <div className="w-24 h-1 bg-[#dc2626] mx-auto mb-8"></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {content.subtitle}
          </p>
        </div>

        {/* About Content */}
        <div className="grid md:grid-cols-2 gap-12 mb-20">
          <div className="space-y-6">
            <h2 className="heading-font text-3xl font-bold text-[#1e4a7a]">Hakkımızda</h2>
            <p className="text-gray-600 leading-relaxed">
              {content.about_text_1}
            </p>
            <p className="text-gray-600 leading-relaxed">
              {content.about_text_2}
            </p>
          </div>
          <div className="space-y-6">
            <h2 className="heading-font text-3xl font-bold text-[#1e4a7a]">Misyonumuz</h2>
            <p className="text-gray-600 leading-relaxed">
              {content.mission_text}
            </p>
            <h2 className="heading-font text-3xl font-bold text-[#1e4a7a] mt-8">Vizyonumuz</h2>
            <p className="text-gray-600 leading-relaxed">
              {content.vision_text}
            </p>
          </div>
        </div>

        {/* Values */}
        <div className="mb-16">
          <h2 className="heading-font text-4xl font-bold text-[#1e4a7a] text-center mb-12">Değerlerimiz</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div
                  key={index}
                  data-testid={`value-card-${index}`}
                  className="text-center p-6 rounded-2xl bg-gradient-to-br from-gray-50 to-white border border-gray-200 hover:border-[#dc2626] hover:shadow-xl transition-all duration-300"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-[#1e4a7a] to-[#2563eb] rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Icon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="heading-font text-xl font-semibold text-[#1e4a7a] mb-2">{value.title}</h3>
                  <p className="text-gray-600 text-sm">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Stats */}
        <div className="bg-gradient-to-r from-[#1e4a7a] to-[#2563eb] rounded-3xl p-12">
          <div className="grid sm:grid-cols-3 gap-8 text-center text-white">
            <div data-testid="stat-experience">
              <div className="heading-font text-5xl font-bold mb-2">{content.stat_experience}</div>
              <div className="text-lg">Yıllık Deneyim</div>
            </div>
            <div data-testid="stat-projects">
              <div className="heading-font text-5xl font-bold mb-2">{content.stat_projects}</div>
              <div className="text-lg">Tamamlanan Proje</div>
            </div>
            <div data-testid="stat-clients">
              <div className="heading-font text-5xl font-bold mb-2">{content.stat_clients}</div>
              <div className="text-lg">Mutlu Müşteri</div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default About;